import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AppText, FlexDiv } from "src/Components/UI";
import { PhoenixInput, PhoenixMultiSelect } from "src/Components/UI/Phoenix";
import { OptionItem } from "src/types";
import { theme } from "src/utils/theme";
import { appToast } from "src/utils/toast";

import { hasValue } from "src/utils/misc";
import styled from "styled-components";
import DynamicDropdown from "./DynamicDropdown";

const CUSTOM_FIELD_NUMBER_OPERATORS = [
  { label: "Equal to", value: "EqualTo" },
  { label: "Not equal to", value: "NotEqualTo" },
  { label: "Greater than", value: "GreaterThan" },
  { label: "Less than", value: "LessThan" },
  { label: "Greater than or equal to", value: "GreaterThanOrEqualTo" },
  { label: "Less than or equal to", value: "LessThanOrEqualTo" },
  { label: "Between", value: "Between" },
  { label: "Not between", value: "NotBetween" },
  { label: "Is known", value: "IsNotEmpty" },
];

interface CustomFieldNumberFilterProps {
  value?: LocalValue;
  onChange?: (value: LocalValue) => void;
  removeFilter?: () => void;
}

const DEFAULT_VALUE = { id: "", key: "", type: "Number", operator: "", value: [] };

interface LocalValue {
  id: string;
  key: string;
  type: string;
  operator: string;
  value: number[];
}

const CustomFieldNumberFilter = ({ value, onChange, removeFilter }: CustomFieldNumberFilterProps) => {
  const [localValue, setLocalValue] = useState<LocalValue>(value || DEFAULT_VALUE);
  const [isOpen, setIsOpen] = useState(!value?.operator);
  const hasChangesRef = useRef(false);
  const isInitialMount = useRef(true);

  useEffect(() => {
    if (isInitialMount.current && !value?.operator) {
      isInitialMount.current = false;
      setIsOpen(true);
    }
  }, [value]);

  useEffect(() => {
    if (!isOpen) {
      setLocalValue(value || DEFAULT_VALUE);
      hasChangesRef.current = false;
    }
  }, [value, isOpen]);

  useEffect(() => {
    hasChangesRef.current = JSON.stringify(localValue) !== JSON.stringify(value);
  }, [localValue, value]);

  const isRangeOperator = localValue?.operator === "Between" || localValue?.operator === "NotBetween";
  const isNoValueOperator = localValue?.operator === "IsNotEmpty" || localValue?.operator === "IsNotNull";

  const selectedOperator = CUSTOM_FIELD_NUMBER_OPERATORS?.find((item) => item.value === localValue?.operator);

  const label = useMemo(() => {
    if (!localValue?.key) return "";

    const operatorText = selectedOperator?.label?.toLowerCase() ?? "";

    if (isNoValueOperator) {
      return `${localValue.key} ${operatorText}`.trim();
    }

    const valueText = isRangeOperator
      ? `${localValue.value[0] ?? ""} - ${localValue.value[1] ?? ""}`
      : localValue.value[0] ?? "";

    return `${localValue.key} ${operatorText} ${valueText}`.trim();
  }, [selectedOperator, localValue, isRangeOperator, isNoValueOperator]);

  const handleIsOpenChange = useCallback(
    (newIsOpen: boolean) => {
      if (newIsOpen) {
        setIsOpen(newIsOpen);
        return;
      }

      if (localValue?.operator) {
        if (!isNoValueOperator) {
          if (!localValue.value || localValue.value.length === 0) {
            appToast("Invalid Number Filter! Must specify a value");
            return;
          }

          if (isRangeOperator && !localValue.value[1] && localValue.value[1] !== 0) {
            appToast("Invalid Number Filter! Must specify a second value for range");
            return;
          }

          let finalValue = { ...localValue };

          if (isRangeOperator && localValue.value[0] > localValue.value[1]) {
            finalValue = {
              ...finalValue,
              value: [localValue.value[1], localValue.value[0]],
            };
          }

          if (hasChangesRef.current) {
            onChange?.(finalValue);
            hasChangesRef.current = false;
          }
        } else {
          if (hasChangesRef.current) {
            onChange?.({ ...localValue, value: [] });
            hasChangesRef.current = false;
          }
        }
      }

      setIsOpen(false);
    },
    [localValue, onChange, isRangeOperator, isNoValueOperator],
  );

  const handleClear = useCallback(() => {
    setLocalValue(DEFAULT_VALUE);
    hasChangesRef.current = true;
  }, []);

  const handleValueChange = useCallback((updates: Partial<LocalValue>) => {
    setLocalValue((prev) => ({ ...prev, ...updates }));
    hasChangesRef.current = true;
  }, []);

  return (
    <DynamicDropdown<string>
      label="Custom Field"
      onClear={handleClear}
      isOpen={isOpen}
      setIsOpen={handleIsOpenChange}
      value={localValue?.id ?? ""}
      onRemove={hasValue(localValue) ? removeFilter : undefined}
      renderHeader={
        <StyledAppText fontSize={12} color={theme.text.brand.primary}>
          {label}
        </StyledAppText>
      }
    >
      <FlexDiv direction="column" gap={8}>
        <PhoenixMultiSelect
          isMulti={false}
          marginBottom={false}
          menuPosition="fixed"
          menuShouldBlockScroll
          name="number_operator"
          options={CUSTOM_FIELD_NUMBER_OPERATORS}
          placeholder="Select Operator"
          value={selectedOperator}
          onChange={(item: OptionItem) => handleValueChange({ operator: item?.value as string, value: [] })}
        />

        {!isNoValueOperator && (
          <FlexDiv align="center" gap={8} width="100%">
            <PhoenixInput
              name="number_value_1"
              inputValueType="number"
              value={Number(localValue?.value?.[0])}
              placeholder="0"
              type="number"
              showNumberArrows
              onChange={(e: any) => {
                const inputValue = e.target.value;

                const newValueSet = [...(localValue?.value || [])];

                newValueSet[0] = parseInt(inputValue) || 0;

                handleValueChange({ value: newValueSet });
              }}
              handleNumberArrowUpClick={() => {
                const newValueSet = [...(localValue?.value || [])];

                const newFirstValue = (parseInt(localValue?.value?.[0]?.toString() ?? "0") || 0) + 1;

                newValueSet[0] = newFirstValue;

                handleValueChange({ value: newValueSet });
              }}
              handleNumberArrowDownClick={() => {
                const newValueSet = [...(localValue?.value || [])];

                const newFirstValue = Math.max(0, (parseInt(localValue?.value?.[0]?.toString() ?? "0") || 0) - 1);

                newValueSet[0] = newFirstValue;

                handleValueChange({ value: newValueSet });
              }}
              displayNoContextText
            />

            {isRangeOperator && (
              <>
                <AppText>and</AppText>
                <PhoenixInput
                  name="number_value_2"
                  inputValueType="number"
                  value={Number(localValue?.value?.[1])}
                  type="number"
                  placeholder="0"
                  showNumberArrows
                  onChange={(e: any) => {
                    const inputValue = e.target.value;

                    const newValueSet = [...(localValue?.value || [])];

                    newValueSet[1] = parseInt(inputValue) || 0;

                    handleValueChange({ value: newValueSet });
                  }}
                  handleNumberArrowUpClick={() => {
                    const newValueSet = [...(localValue?.value || [])];

                    const newSecondValueIncremented = (parseInt(localValue?.value?.[1]?.toString() ?? "0") || 0) + 1;

                    newValueSet[1] = newSecondValueIncremented;

                    handleValueChange({ value: newValueSet });
                  }}
                  handleNumberArrowDownClick={() => {
                    const newValueSet = [...(localValue?.value || [])];

                    const newSecondValueDecremented = Math.max(
                      0,
                      (parseInt(localValue?.value?.[1]?.toString() ?? "0") || 0) - 1,
                    );

                    newValueSet[1] = newSecondValueDecremented;

                    handleValueChange({ value: newValueSet });
                  }}
                  displayNoContextText
                />
              </>
            )}
          </FlexDiv>
        )}
      </FlexDiv>
    </DynamicDropdown>
  );
};

const StyledAppText = styled(AppText)`
  max-width: 180px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export default CustomFieldNumberFilter;
